import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Grid,
} from '@mui/material';
import { ScrapingTaskCreate } from '../hooks/useScrapingTasks';
import { useResolvedDefinition, useResolvedDefinitions } from '../hooks/useMarketplaceDefinitions';
import { storeClient, endpoints, isGodUser } from '../api/client';
import { generateSearchUrlsForMarketplace, resolveSearchUrlTemplate } from '../utils/urlTemplates';

const DEFAULT_MAX_STAGE2_PRODUCTS = 5;

function definitionIsActive(definition: any): boolean {
  return definition?.is_active !== false;
}

function definitionNeedsFix(definition: any): boolean {
  const health = String(definition?.scrape_health || '').toLowerCase();
  return health === 'needs_maintenance';
}

function definitionOptionLabel(definition: any): string {
  const name = definition?.name || '';
  const tags: string[] = [];
  if (!definitionIsActive(definition)) tags.push('inactive');
  if (definitionNeedsFix(definition)) tags.push('needs fix');
  return tags.length ? `${name} (${tags.join(', ')})` : name;
}

export interface TaskFormSubmitData {
  taskData: ScrapingTaskCreate | Partial<ScrapingTaskCreate>;
  selectedMarketplaces: any[]; // For create mode with multiple marketplaces
  groupInfo?: { createGroup: boolean; groupName: string };
}

export interface TaskFormProps {
  mode: 'create' | 'edit';
  definitions: any[];
  loading?: boolean;
  initial?: Partial<ScrapingTaskCreate> & {
    brandName?: string;
    productName?: string;
    keywords_text?: string;
    execution_mode?: 'internal' | 'extension' | string;
    extension_promoted_reason?: string | null;
    extension_any_marketplace?: boolean;
  };
  onCancel: () => void;
  onSubmit: (submitData: TaskFormSubmitData) => void;
}

const TaskForm: React.FC<TaskFormProps> = ({
  mode,
  definitions,
  loading = false,
  initial,
  onCancel,
  onSubmit,
}) => {
  // For create mode, support multiple marketplaces; for edit mode, single marketplace
  const [selectedMarketplaces, setSelectedMarketplaces] = useState<any[]>(
    initial?.marketplace_definition_id
      ? definitions.filter(d => d.id === initial.marketplace_definition_id)
      : []
  );

  const [formData, setFormData] = useState<Partial<ScrapingTaskCreate>>({
    marketplace_definition_id: initial?.marketplace_definition_id || '',
    urls: initial?.urls || [''],
    max_pages: initial?.max_pages ?? 1,
    max_stage2_products:
      initial?.max_stage2_products ??
      ((initial?.input_mode as string) === 'urls' ? 1 : DEFAULT_MAX_STAGE2_PRODUCTS),
    timeout_minutes: initial?.timeout_minutes ?? 10,
  });
  const [inputMode, setInputMode] = useState<'urls' | 'keywords'>(
    (initial?.input_mode as 'urls' | 'keywords') || 'keywords'
  );
  const [keywordsText, setKeywordsText] = useState<string>(initial?.keywords_text || '');

  const [brandName, setBrandName] = useState<string>(initial?.brandName || '');
  const [productName, setProductName] = useState<string>(initial?.productName || '');
  const [brands, setBrands] = useState<Array<{ id?: string; name: string }>>([]);
  const [products, setProducts] = useState<
    Array<{ id?: string; name: string; keywords?: string[] }>
  >([]);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);

  // Track URLs that couldn't be matched to any marketplace
  const [unmatchedUrls, setUnmatchedUrls] = useState<string[]>([]);

  // Group creation options (only for create mode with multiple marketplaces)
  const [createGroup, setCreateGroup] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>('');

  // Loading state for submission
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [runViaExtension, setRunViaExtension] = useState<boolean>(
    initial?.execution_mode === 'extension'
  );
  const [anyExtensionInstance, setAnyExtensionInstance] = useState<boolean>(
    initial?.execution_mode === 'extension' && Boolean(initial?.extension_any_marketplace)
  );

  useEffect(() => {
    setRunViaExtension(initial?.execution_mode === 'extension');
  }, [initial?.execution_mode, initial?.extension_promoted_reason]);

  const captchaFallback =
    mode === 'edit' &&
    initial?.execution_mode === 'extension' &&
    initial?.extension_promoted_reason === 'captcha';

  // Use resolved definition (merged with parent) so search_url_template from parent is available
  const { data: selectedDefinition } = useResolvedDefinition(
    formData.marketplace_definition_id || ''
  );
  const selectedMarketplaceIds = selectedMarketplaces.map(m => m.id).filter(Boolean);
  const { data: resolvedById, isLoading: loadingResolvedTemplates } = useResolvedDefinitions(
    mode === 'create' ? selectedMarketplaceIds : []
  );

  // Sort marketplace definitions alphabetically by name for the select list
  const sortedDefinitions = useMemo(
    () =>
      [...(definitions || [])].sort((a: any, b: any) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
          sensitivity: 'base',
        })
      ),
    [definitions]
  );

  // Load brands once - GOD users get all brands, others get user-assigned brands
  useEffect(() => {
    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        let brandsList: Array<{ id?: string; name: string }> = [];

        if (isGodUser()) {
          // GOD users: fetch list of brand names from BrandController /names
          const res = await storeClient.get(endpoints.storeAllBrandNames());
          const names = res?.data || [];
          brandsList = names.map((name: string) => ({ name }));
        } else {
          // Regular users: fetch user-assigned brands
          const res = await storeClient.get(endpoints.storeBrands());
          const items = res?.data?.items || res?.data?.content || res?.data || [];
          brandsList = items.map((b: any) => ({ id: b.id, name: b.name }));
        }

        // Sort brands alphabetically by name
        brandsList.sort((a, b) => a.name.localeCompare(b.name));
        setBrands(brandsList);

        // Auto-select brand if there's only one and no brand is currently selected
        if (brandsList.length === 1 && !brandName) {
          setBrandName(brandsList[0].name);
        }
      } catch (_) {
        setBrands([]);
      } finally {
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync brand/product from definition for convenience
  useEffect(() => {
    const def: any = selectedDefinition || {};
    const defBrandName: string | undefined = def.brand_name || def.brand;
    const defProductName: string | undefined = def.product_name || def.product;
    if (!initial?.brandName && defBrandName && !brandName) setBrandName(defBrandName);
    if (!initial?.productName && defProductName && !productName) setProductName(defProductName);
  }, [selectedDefinition, brandName, initial?.brandName, initial?.productName, productName]);

  // Adjust max_stage2_products when input mode changes (create mode only).
  // In edit mode, preserve the loaded value - user may have set 1 product intentionally.
  const prevInputModeRef = React.useRef(inputMode);
  useEffect(() => {
    if (mode === 'edit') return;
    const prevInputMode = prevInputModeRef.current;
    prevInputModeRef.current = inputMode;
    if (prevInputMode === inputMode) return;

    // Only auto-adjust when switching modes - check if value is default for other mode
    // Check if current value is the default for the previous mode
    const currentValue = formData.max_stage2_products ?? DEFAULT_MAX_STAGE2_PRODUCTS;

    if (inputMode === 'urls') {
      // Switching to URLs mode: if value is still at keywords default, change to 1
      if (currentValue === DEFAULT_MAX_STAGE2_PRODUCTS) {
        setFormData(prev => ({ ...prev, max_stage2_products: 1 }));
      }
    } else {
      // Switching to keywords mode: if value is at URLs default (1), change to keywords default
      if (currentValue === 1) {
        setFormData(prev => ({ ...prev, max_stage2_products: DEFAULT_MAX_STAGE2_PRODUCTS }));
      }
    }
  }, [inputMode, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load products for selected brand
  useEffect(() => {
    if (!brandName) {
      setProducts([]);
      setProductName('');
      return;
    }
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await storeClient.get(endpoints.storeProductsByBrandName(brandName, 0, 500));
        const items = res?.data?.items || res?.data?.content || res?.data || [];
        const productsList = items.map((p: any) => ({
          id: p.id,
          name: p.name,
          keywords: p.keywords,
        }));
        setProducts(productsList);

        // Preserve existing product if it's still valid for this brand
        const existingStillValid = productsList.some((p: any) => p.name === productName);
        if (!existingStillValid) {
          // Auto-select product if there's only one for this brand
          if (productsList.length === 1) {
            setProductName(productsList[0].name);
          } else {
            setProductName('');
          }
        }
      } catch (_) {
        setProducts([]);
        setProductName('');
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, [brandName]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveTemplate = useMemo(
    () => resolveSearchUrlTemplate(selectedDefinition as any),
    [selectedDefinition]
  );

  // Prefill single-line keywords from selected product when switching to keyword mode
  useEffect(() => {
    if (inputMode !== 'keywords') return;
    if (keywordsText.trim()) return;
    const prod = products.find(p => p.name === productName);
    if (prod && Array.isArray(prod.keywords) && prod.keywords.length > 0) {
      const line = prod.keywords.join(' ').replace(/\s+/g, ' ').trim();
      setKeywordsText(line);
    }
  }, [inputMode, productName, products, keywordsText]);

  const keywordList = useMemo(
    () =>
      keywordsText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean),
    [keywordsText]
  );

  const generatedUrls = useMemo(() => {
    if (inputMode !== 'keywords') return [] as string[];
    return generateSearchUrlsForMarketplace(selectedDefinition as any, keywordsText);
  }, [inputMode, selectedDefinition, keywordsText]);

  // Generate preview URLs for each selected marketplace
  const multiMarketplacePreview = useMemo(() => {
    if (
      mode !== 'create' ||
      selectedMarketplaces.length <= 1 ||
      inputMode !== 'keywords' ||
      !keywordsText.trim()
    ) {
      return [];
    }

    const firstKeyword = keywordList[0];
    if (!firstKeyword) return [];

    return selectedMarketplaces.map(marketplace => {
      const resolved = resolvedById?.[marketplace.id] || marketplace;
      const urls = generateSearchUrlsForMarketplace(resolved, firstKeyword);
      return { name: marketplace.name, url: urls[0] || '' };
    });
  }, [mode, selectedMarketplaces, inputMode, keywordsText, keywordList, resolvedById]);

  const hasBrandAndProduct = Boolean(brandName.trim() && productName.trim());

  const hasUrlsInput = useMemo(() => {
    if (selectedMarketplaces.length > 1) {
      return inputMode === 'keywords'
        ? keywordList.length > 0
        : (formData.urls || []).filter(u => u.trim()).length > 0;
    }
    return inputMode === 'urls'
      ? (formData.urls || []).some(u => u.trim()) && !(formData.urls || []).some(u => !u.trim())
      : generatedUrls.length > 0;
  }, [selectedMarketplaces.length, inputMode, generatedUrls, formData.urls, keywordList.length]);

  const waitingOnSearchTemplates =
    mode === 'create' &&
    selectedMarketplaces.length > 1 &&
    inputMode === 'keywords' &&
    loadingResolvedTemplates;

  const canSubmitTask = useMemo(
    () =>
      (selectedMarketplaces.length > 0 || Boolean(formData.marketplace_definition_id)) &&
      hasUrlsInput &&
      hasBrandAndProduct &&
      (!createGroup || Boolean(groupName.trim())) &&
      !waitingOnSearchTemplates,
    [
      selectedMarketplaces.length,
      formData.marketplace_definition_id,
      hasUrlsInput,
      hasBrandAndProduct,
      createGroup,
      groupName,
      waitingOnSearchTemplates,
    ]
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasBrandAndProduct) return;

    setIsSubmitting(true);
    try {
      // Allow URL mode with multiple marketplaces - parent handler will group by domain
      const effectiveInputMode = inputMode;

      // For URL mode with multiple marketplaces, pass URLs - parent handler will group by domain
      // For keywords mode with multiple marketplaces, pass empty URLs - parent handler will generate per marketplace
      // For single marketplace, use URLs as-is
      const urlsToUse =
        selectedMarketplaces.length > 1
          ? effectiveInputMode === 'keywords'
            ? [] // Multi-marketplace keywords: parent generates URLs per marketplace
            : (formData.urls || []).filter(u => u.trim()) // Multi-marketplace URLs: parent groups by domain
          : effectiveInputMode === 'keywords'
            ? generatedUrls
            : (formData.urls || []).filter(u => u.trim());

      // Validate group name if creating a group
      if (mode === 'create' && createGroup && !groupName.trim()) {
        return; // Form validation will show error
      }

      // Note: Brand/product information is stored in the scraping task itself,
      // not in the marketplace definition config, to keep definitions reusable
      if (mode === 'create') {
        const payload: ScrapingTaskCreate = {
          marketplace_definition_id: formData.marketplace_definition_id || '',
          urls: urlsToUse,
          max_pages: formData.max_pages || 1,
          max_stage2_products: formData.max_stage2_products ?? DEFAULT_MAX_STAGE2_PRODUCTS,
          timeout_minutes: formData.timeout_minutes || 10,
          input_mode: effectiveInputMode,
          paused: true, // do not start immediately
          execution_mode: runViaExtension ? 'extension' : 'internal',
          extension_any_marketplace: runViaExtension && anyExtensionInstance,
          brand_name: brandName.trim(),
          product_name: productName.trim(),
          ...(effectiveInputMode === 'keywords' && keywordsText
            ? { keywords_text: keywordsText }
            : {}),
        };

        // Pass all data including selected marketplaces and group info
        const groupInfo = createGroup
          ? { createGroup: true, groupName: groupName.trim() }
          : undefined;

        const marketplacesForSubmit = selectedMarketplaces.map(m => resolvedById?.[m.id] || m);

        await onSubmit({
          taskData: payload,
          selectedMarketplaces: marketplacesForSubmit,
          groupInfo,
        });
      } else {
        // Edit mode: only include changed fields
        const updates: Partial<ScrapingTaskCreate> = {};

        if (initial?.marketplace_definition_id !== formData.marketplace_definition_id)
          updates.marketplace_definition_id = formData.marketplace_definition_id;
        if (urlsToUse.length > 0) updates.urls = urlsToUse;
        if (initial?.max_pages !== formData.max_pages) updates.max_pages = formData.max_pages;
        if (initial?.max_stage2_products !== formData.max_stage2_products)
          updates.max_stage2_products = formData.max_stage2_products;
        if (initial?.timeout_minutes !== formData.timeout_minutes)
          updates.timeout_minutes = formData.timeout_minutes;

        (updates as any).brand_name = brandName.trim();
        (updates as any).product_name = productName.trim();

        // Always include input mode and keywords_text for edit
        (updates as any).input_mode = effectiveInputMode;
        if (effectiveInputMode === 'keywords') (updates as any).keywords_text = keywordsText || '';

        const initialExtension = initial?.execution_mode === 'extension';
        if (runViaExtension !== initialExtension) {
          updates.execution_mode = runViaExtension ? 'extension' : 'internal';
        }
        const nextAnyMarketplace = runViaExtension && anyExtensionInstance;
        if (nextAnyMarketplace !== Boolean(initial?.extension_any_marketplace)) {
          updates.extension_any_marketplace = nextAnyMarketplace;
        }

        await onSubmit({
          taskData: updates,
          selectedMarketplaces: [],
          groupInfo: undefined,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Task Name removed as per requirement; tasks are organized by brand/keywords */}

          {/* Marketplace selection: Autocomplete for create mode (multi-select), Select for edit mode */}
          {mode === 'create' ? (
            <Autocomplete
              multiple
              disablePortal
              autoSelect
              autoHighlight
              options={sortedDefinitions}
              getOptionLabel={(option: any) => definitionOptionLabel(option)}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              value={selectedMarketplaces}
              onChange={(_, newValue) => {
                setSelectedMarketplaces(newValue);
                // Update formData for backward compatibility (use first selected)
                setFormData(prev => ({
                  ...prev,
                  marketplace_definition_id: newValue.length > 0 ? newValue[0].id : '',
                }));
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Marketplace Definitions"
                  placeholder="Select marketplaces..."
                  required={selectedMarketplaces.length === 0}
                  helperText="Inactive and Needs fix marketplaces are allowed as an override"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={definitionOptionLabel(option)}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Marketplace Definition</InputLabel>
              <Select
                value={formData.marketplace_definition_id}
                onChange={e =>
                  setFormData(prev => ({ ...prev, marketplace_definition_id: e.target.value }))
                }
                label="Marketplace Definition"
              >
                {sortedDefinitions.map(def => (
                  <MenuItem key={def.id} value={def.id}>
                    {definitionOptionLabel(def)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {(mode === 'create'
            ? selectedMarketplaces
            : sortedDefinitions.filter(d => d.id === formData.marketplace_definition_id)
          ).some(d => !definitionIsActive(d) || definitionNeedsFix(d)) && (
            <Alert severity="warning">
              This marketplace is inactive or needs a fix. Saving still runs it. A successful scrape
              clears Needs fix; turn Active on in the definition to use it in Auto brand scan.
            </Alert>
          )}

          {/* Brand / Product selectors — required for review Store matching */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={brandName}
                  onChange={e => {
                    const next = e.target.value as string;
                    setBrandName(next);
                    if (next !== brandName) setProductName('');
                  }}
                  label="Brand"
                >
                  <MenuItem value="" disabled>
                    <em>{loadingBrands ? 'Loading…' : 'Select a brand'}</em>
                  </MenuItem>
                  {brands.map(b => (
                    <MenuItem key={b.name} value={b.name}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required disabled={!brandName}>
                <InputLabel>Product</InputLabel>
                <Select
                  value={productName}
                  onChange={e => setProductName(e.target.value as string)}
                  label="Product"
                >
                  <MenuItem value="" disabled>
                    <em>
                      {brandName
                        ? loadingProducts
                          ? 'Loading…'
                          : 'Select a product'
                        : 'Select a brand first'}
                    </em>
                  </MenuItem>
                  {products.map(p => (
                    <MenuItem key={p.name} value={p.name}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Group creation options (only for create mode with multiple marketplaces) */}
          {mode === 'create' && selectedMarketplaces.length > 1 && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createGroup}
                    onChange={e => setCreateGroup(e.target.checked)}
                  />
                }
                label="Create task group"
              />
              {createGroup && (
                <TextField
                  fullWidth
                  label="Group Name"
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="e.g., China Marketplaces"
                  required={createGroup}
                  sx={{ mt: 2 }}
                  helperText="A group will be created containing all tasks created from the selected marketplaces"
                />
              )}
            </Box>
          )}

          {/* Input mode selector */}
          <FormControl fullWidth>
            <InputLabel>Input Mode</InputLabel>
            <Select
              value={inputMode}
              onChange={e => {
                setInputMode(e.target.value as 'urls' | 'keywords');
                // Clear unmatched URLs when switching input modes
                if (unmatchedUrls.length > 0) {
                  setUnmatchedUrls([]);
                }
              }}
              label="Input Mode"
            >
              <MenuItem value="urls">Enter URLs</MenuItem>
              <MenuItem value="keywords">Enter Keywords</MenuItem>
            </Select>
          </FormControl>
          {mode === 'create' && selectedMarketplaces.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {inputMode === 'keywords'
                ? 'Each marketplace will get its own task with generated URLs from keywords'
                : 'URLs will be automatically grouped by marketplace domain'}
            </Typography>
          )}

          {inputMode === 'urls' ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                URLs to Scrape
              </Typography>
              {mode === 'create' && selectedMarketplaces.length === 0 && (
                <Typography variant="caption" color="primary" sx={{ mb: 1, display: 'block' }}>
                  💡 Tip: Paste your URLs and marketplaces will be automatically detected and
                  selected
                </Typography>
              )}
              {mode === 'create' && selectedMarketplaces.length > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  URLs will be automatically grouped by marketplace - {selectedMarketplaces.length}{' '}
                  marketplaces selected
                </Typography>
              )}
              {(formData.urls || []).map((url, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    value={url}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        urls: (prev.urls || []).map((u, i) => (i === index ? e.target.value : u)),
                      }));
                      // Clear unmatched URLs when user manually edits
                      if (unmatchedUrls.length > 0) {
                        setUnmatchedUrls([]);
                      }
                    }}
                    onPaste={e => {
                      const pastedText = e.clipboardData.getData('text');
                      // Check if pasted content contains multiple URLs (newlines or tabs)
                      const urlsFromPaste = pastedText
                        .split(/[\n\r\t]+/)
                        .map(line => line.trim())
                        .filter(line => line.length > 0);

                      // Auto-detect and select marketplaces from URLs (for create mode)
                      const autoSelectMarketplaces = (urls: string[]) => {
                        if (mode !== 'create') return;

                        const getDomainFromUrl = (url: string): string => {
                          try {
                            const urlObj = new URL(url);
                            return urlObj.hostname.replace(/^www\./, '');
                          } catch {
                            return '';
                          }
                        };

                        // Find matching marketplace definitions for the pasted URLs
                        const matchedMarketplaces = new Set<any>();
                        const unmatched: string[] = [];

                        for (const url of urls) {
                          const urlDomain = getDomainFromUrl(url);
                          if (!urlDomain) {
                            unmatched.push(url);
                            continue;
                          }

                          // Find marketplace definition that matches this domain
                          const matchingDef = definitions.find(def => {
                            const defDomain = (def.domain || '').replace(/^www\./, '');
                            return defDomain && urlDomain.includes(defDomain);
                          });

                          if (matchingDef) {
                            matchedMarketplaces.add(matchingDef);
                          } else {
                            unmatched.push(url);
                          }
                        }

                        // Update unmatched URLs state
                        setUnmatchedUrls(unmatched);

                        // Auto-select the matched marketplaces
                        if (matchedMarketplaces.size > 0) {
                          const marketplacesArray = Array.from(matchedMarketplaces);
                          setSelectedMarketplaces(marketplacesArray);

                          // If only one marketplace, also set it in formData
                          if (marketplacesArray.length === 1) {
                            setFormData(prev => ({
                              ...prev,
                              marketplace_definition_id: marketplacesArray[0].id,
                            }));
                          }

                          // Auto-enable task group creation when multiple marketplaces detected
                          if (marketplacesArray.length > 1) {
                            setCreateGroup(true);
                            // Generate a default group name if empty
                            if (!groupName.trim()) {
                              const marketplaceNames = marketplacesArray
                                .map(m => m.name)
                                .join(', ');
                              setGroupName(`Tasks - ${marketplaceNames}`);
                            }
                          }

                          console.log(
                            '[Auto-select] Matched marketplaces:',
                            marketplacesArray.map(m => m.name)
                          );
                          if (unmatched.length > 0) {
                            console.warn('[Auto-select] Unmatched URLs:', unmatched);
                          }
                        }
                      };

                      if (urlsFromPaste.length > 1) {
                        e.preventDefault();

                        // Auto-detect marketplaces and separate matched/unmatched URLs
                        if (mode === 'create') {
                          const getDomainFromUrl = (url: string): string => {
                            try {
                              const urlObj = new URL(url);
                              return urlObj.hostname.replace(/^www\./, '');
                            } catch {
                              return '';
                            }
                          };

                          const unmatched: string[] = [];
                          const matched: string[] = [];
                          const matchedMarketplaces = new Set<any>();

                          for (const url of urlsFromPaste) {
                            const urlDomain = getDomainFromUrl(url);
                            if (!urlDomain) {
                              unmatched.push(url);
                              continue;
                            }

                            const matchingDef = definitions.find(def => {
                              const defDomain = (def.domain || '').replace(/^www\./, '');
                              return defDomain && urlDomain.includes(defDomain);
                            });

                            if (matchingDef) {
                              matched.push(url);
                              matchedMarketplaces.add(matchingDef);
                            } else {
                              unmatched.push(url);
                            }
                          }

                          // Set only matched URLs in the form
                          setFormData(prev => ({
                            ...prev,
                            urls: matched.length > 0 ? matched : [''],
                          }));

                          // Set unmatched URLs in the warning state
                          setUnmatchedUrls(unmatched);

                          // Set input mode to URLs
                          setInputMode('urls');

                          // Auto-select the matched marketplaces
                          if (matchedMarketplaces.size > 0) {
                            const marketplacesArray = Array.from(matchedMarketplaces);
                            setSelectedMarketplaces(marketplacesArray);

                            // If only one marketplace, also set it in formData
                            if (marketplacesArray.length === 1) {
                              setFormData(prev => ({
                                ...prev,
                                marketplace_definition_id: marketplacesArray[0].id,
                              }));
                            }

                            // Auto-enable task group creation when multiple marketplaces detected
                            if (marketplacesArray.length > 1) {
                              setCreateGroup(true);
                              // Generate a default group name if empty
                              if (!groupName.trim()) {
                                const marketplaceNames = marketplacesArray
                                  .map(m => m.name)
                                  .join(', ');
                                setGroupName(`Tasks - ${marketplaceNames}`);
                              }
                            }

                            console.log(
                              '[Auto-select] Matched marketplaces:',
                              marketplacesArray.map(m => m.name)
                            );
                            if (unmatched.length > 0) {
                              console.warn('[Auto-select] Unmatched URLs:', unmatched);
                            }
                          }
                        } else {
                          // Edit mode - just set the URLs
                          setFormData(prev => ({
                            ...prev,
                            urls: urlsFromPaste,
                          }));
                          setInputMode('urls');
                        }
                      } else if (urlsFromPaste.length === 1) {
                        // Single URL - still auto-detect marketplace but allow default paste
                        setInputMode('urls');
                        if (mode === 'create') {
                          autoSelectMarketplaces(urlsFromPaste);
                        }
                      }
                    }}
                    placeholder="https://example.com/search?q=... (paste multiple URLs - marketplaces auto-detected)"
                    required
                    fullWidth
                  />
                  {(formData.urls?.length || 0) > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          urls: (prev.urls || []).filter((_, i) => i !== index),
                        }))
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  setFormData(prev => ({ ...prev, urls: [...(prev.urls || []), ''] }));
                  // Clear unmatched URLs when adding a new field
                  if (unmatchedUrls.length > 0) {
                    setUnmatchedUrls([]);
                  }
                }}
                size="small"
              >
                Add URL
              </Button>

              {/* Display unmatched URLs warning */}
              {unmatchedUrls.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'warning.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                    ⚠️ Unmatched URLs ({unmatchedUrls.length})
                  </Typography>
                  <Typography
                    variant="caption"
                    color="warning.dark"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    The following URLs could not be matched to any marketplace definition and will
                    not be processed:
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      backgroundColor: 'background.paper',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {unmatchedUrls.map((url, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          wordBreak: 'break-all',
                        }}
                      >
                        {url}
                      </Typography>
                    ))}
                  </Box>
                  <Button
                    variant="text"
                    size="small"
                    color="warning"
                    onClick={() => {
                      // Remove unmatched URLs from the form
                      setFormData(prev => ({
                        ...prev,
                        urls: (prev.urls || []).filter(u => !unmatchedUrls.includes(u)),
                      }));
                      setUnmatchedUrls([]);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove Unmatched URLs
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Keywords (one search per line)
              </Typography>
              <TextField
                value={keywordsText}
                onChange={e => setKeywordsText(e.target.value)}
                placeholder={'e.g.\niphone 15 case\nsamsung galaxy s24 cover'}
                fullWidth
                multiline
                minRows={4}
              />
              <Box sx={{ mt: 1 }}>
                {/* Show multi-marketplace preview or single marketplace preview */}
                {multiMarketplacePreview.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      URL Preview (first keyword for each marketplace)
                    </Typography>
                    {multiMarketplacePreview.map((preview, i) => (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        >
                          {preview.name}:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', ml: 2, wordBreak: 'break-all' }}
                        >
                          {preview.url}
                        </Typography>
                      </Box>
                    ))}
                    {keywordList.length > 1 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        + {keywordList.length - 1} more keyword{keywordList.length > 2 ? 's' : ''}{' '}
                        per marketplace
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Using template: {resolveTemplate}
                    </Typography>
                    {generatedUrls.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">
                          Preview ({generatedUrls.length})
                        </Typography>
                        {generatedUrls.slice(0, 3).map((u, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                            {u}
                          </Typography>
                        ))}
                        {generatedUrls.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            ...and {generatedUrls.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Max Pages"
                type="number"
                value={formData.max_pages || 1}
                onChange={e =>
                  setFormData(prev => ({ ...prev, max_pages: parseInt(e.target.value) || 1 }))
                }
                inputProps={{ min: 1, max: 50 }}
                fullWidth
                helperText="Search pages"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Max Stage 2 Products"
                type="number"
                value={formData.max_stage2_products ?? DEFAULT_MAX_STAGE2_PRODUCTS}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    max_stage2_products: parseInt(e.target.value) || 0,
                  }))
                }
                inputProps={{ min: 0, max: 200 }}
                fullWidth
                helperText="Product details (0 = disabled)"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Timeout (Minutes)"
                type="number"
                value={formData.timeout_minutes || 10}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    timeout_minutes: parseInt(e.target.value) || 10,
                  }))
                }
                inputProps={{ min: 1, max: 120 }}
                fullWidth
                helperText="Task timeout"
              />
            </Grid>
          </Grid>

          {captchaFallback && (
            <Alert severity="info">
              This task was automatically switched to extension mode after in-cluster scraping hit a
              CAPTCHA. Keep &quot;Run via Chrome extension&quot; checked and enable the scraper task
              listener in the Collector extension to retry from a residential browser.
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={runViaExtension}
                onChange={e => {
                  setRunViaExtension(e.target.checked);
                  if (!e.target.checked) setAnyExtensionInstance(false);
                }}
              />
            }
            label="Run via Chrome extension (residential browser)"
          />
          {runViaExtension && (
            <FormControlLabel
              sx={{ display: 'flex', ml: 3 }}
              control={
                <Checkbox
                  checked={anyExtensionInstance}
                  onChange={e => setAnyExtensionInstance(e.target.checked)}
                />
              }
              label="Any extension instance"
            />
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {captchaFallback
              ? 'Extension mode was enabled automatically; you can uncheck to return to in-cluster scraping on the next run.'
              : runViaExtension && anyExtensionInstance
                ? 'Any listening extension browser can take this task, including marketplaces missing from its list.'
                : 'Skips in-cluster scraping. Enable the scraper task listener in the Collector extension options on a residential machine.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {mode === 'create' && (
          <Button
            onClick={async () => {
              // Validate group name if creating a group
              if (createGroup && !groupName.trim()) {
                return;
              }
              if (!hasBrandAndProduct) {
                return;
              }

              setIsSubmitting(true);
              try {
                // Save and run: submit with paused=false
                const effectiveInputMode = inputMode;

                // For URL mode with multiple marketplaces, pass empty URLs - parent handler will group by domain
                // For keywords mode with multiple marketplaces, pass empty URLs - parent handler will generate per marketplace
                // For single marketplace, use URLs as-is
                const urlsToUse =
                  selectedMarketplaces.length > 1
                    ? effectiveInputMode === 'keywords'
                      ? [] // Multi-marketplace keywords: parent generates URLs per marketplace
                      : (formData.urls || []).filter(u => u.trim()) // Multi-marketplace URLs: parent groups by domain
                    : effectiveInputMode === 'keywords'
                      ? generatedUrls
                      : (formData.urls || []).filter(u => u.trim());

                const payload: ScrapingTaskCreate = {
                  marketplace_definition_id: formData.marketplace_definition_id || '',
                  urls: urlsToUse,
                  max_pages: formData.max_pages || 1,
                  max_stage2_products: formData.max_stage2_products ?? DEFAULT_MAX_STAGE2_PRODUCTS,
                  timeout_minutes: formData.timeout_minutes || 10,
                  input_mode: effectiveInputMode,
                  paused: false,
                  execution_mode: runViaExtension ? 'extension' : 'internal',
                  extension_any_marketplace: runViaExtension && anyExtensionInstance,
                  brand_name: brandName.trim(),
                  product_name: productName.trim(),
                  ...(effectiveInputMode === 'keywords' && keywordsText
                    ? { keywords_text: keywordsText }
                    : {}),
                } as ScrapingTaskCreate;

                // Pass all data including selected marketplaces and group info
                const groupInfo = createGroup
                  ? { createGroup: true, groupName: groupName.trim() }
                  : undefined;

                await onSubmit({
                  taskData: payload,
                  selectedMarketplaces: selectedMarketplaces.map(m => resolvedById?.[m.id] || m),
                  groupInfo,
                });
              } finally {
                setIsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={loading || isSubmitting || !canSubmitTask}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            {isSubmitting ? 'Creating...' : 'Save and Run'}
          </Button>
        )}
        <Button
          type="submit"
          variant="outlined"
          disabled={loading || isSubmitting || !canSubmitTask}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting ? 'Creating...' : 'Save'}
        </Button>
      </DialogActions>
    </form>
  );
};

export default TaskForm;
